import { Effect, pipe } from 'effect';

// Constants
import { END_POINTS } from '@/constants/endpoint';
import { DEFAULT_USERS_LIMIT, MESSAGES, STATUS_CODE } from '@shared/constants';

// Services
import { httpClient } from '@/services/http';

// Types
import { UserProfile, UserListResponse } from '@/types/api';

// Utils
import { RequestConfig } from '@/types';

const getUserProfile = async (options?: {
  requestConfig?: RequestConfig;
  username?: string;
}) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const response = await httpClient<UserProfile>(
            END_POINTS.GET_USER_PROFILE(options?.username),
            options?.requestConfig,
          );

          return response;
        },
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

const getUserSimpleProfile = async (
  identity: string,
  requestConfig: RequestConfig,
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const response = await httpClient<UserProfile>(
            END_POINTS.GET_USER_SIMPLE_PROFILE(identity),
            requestConfig,
          );

          return response;
        },
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

const getAllUsers = async (options?: { requestConfig?: RequestConfig }) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const allUsers: UserProfile[] = [];
          let nextCursor: string | undefined;

          do {
            const response = await httpClient<UserListResponse>(
              END_POINTS.GET_USERS(DEFAULT_USERS_LIMIT, nextCursor),
              { ...options?.requestConfig, isFullResponse: true },
            );

            if (!response?.data?.data) {
              break;
            }

            // Add all users from current page
            allUsers.push(...response.data.data);

            // Get next cursor for pagination
            const { pagination } = response.data.meta || {};
            nextCursor = pagination?.cursor;
          } while (nextCursor);

          return { data: allUsers };
        },
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

const getAllSimpleUsers = async (options?: { requestConfig?: RequestConfig }) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const allUsers: UserProfile[] = [];
          let nextCursor: string | undefined;

          do {
            const response = await httpClient<UserListResponse>(
              END_POINTS.GET_SIMPLE_USERS(DEFAULT_USERS_LIMIT, nextCursor),
              { ...options?.requestConfig, isFullResponse: true },
            );

            if (!response?.data?.data) {
              break;
            }

            // Add all users from current page
            allUsers.push(...response.data.data);

            // Get next cursor for pagination
            const { pagination } = response.data.meta || {};
            nextCursor = pagination?.cursor;
          } while (nextCursor);

          return { data: allUsers, error: null };
        },
        catch: (error) => ({
          data: null,
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
          status: STATUS_CODE.INTERNAL_SERVER_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export { getUserProfile, getAllUsers, getUserSimpleProfile, getAllSimpleUsers };
